// frontend/src/components/masters/ShippingLineList.js
import React, { useState, useEffect } from 'react';
import ShippingLineForm from './ShippingLineForm'; // Import the form component
import Select2Dropdown from '../common/Select2Dropdown'; // Add this import if not already present
import useSortableTable from '../common/useSortableTable';
import './ShippingLineList.css'; // Component-specific CSS for the list
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

// Placeholder API endpoint - replace with your actual Django API endpoint
const API_BASE_URL = '/api/masters/shippinglines/'; // Added trailing slash

// Helper to get auth token (re-used from other forms/lists)
const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
    'Content-Type': 'application/json'
});

const DEPOSIT_WAIVER_OPTIONS = [
    { id: '', text: 'ALL' },
    { id: 'YES', text: 'YES' },
    { id: 'NO', text: 'NO' },
    { id: 'N/A', text: 'N/A' }
];

const LINE_TYPE_OPTIONS = [
    { id: '', text: 'ALL' },
    { id: 'SHIPPING LINE', text: 'SHIPPING LINE' },
    { id: 'AGENT', text: 'LOCAL AGENT/CONSOLIDATOR' }
];

const ShippingLineList = () => {
    const [shippingLines, setShippingLines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentShippingLine, setCurrentShippingLine] = useState(null);
    const [formMode, setFormMode] = useState('add'); // 'add', 'edit', 'view'
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [depositWaiverFilter, setDepositWaiverFilter] = useState('');
    const [lineTypeFilter, setLineTypeFilter] = useState('');
    const { hasPermission } = usePermissions(); // 2. Get hook

    // Fetch data from Django backend
    const fetchShippingLines = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(API_BASE_URL, {
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to fetch shipping lines');
            }
            const data = await response.json();
            // Assign a unique temporary ID if not already present, useful for lists before saving
            const processedData = data.map(item => ({ ...item, id: item.id || crypto.randomUUID() }));
            setShippingLines(processedData);
        } catch (err) {
            console.error("Error fetching shipping lines:", err);
            setError("Failed to load shipping lines: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchShippingLines();
    }, []);

   const handleAddShippingLine = () => {
        setCurrentShippingLine(null); // Clear any previous data
        setFormMode('add');
        setIsModalOpen(true);
    };

    const handleEditShippingLine = (shippingLine) => {
        setCurrentShippingLine(shippingLine);
        setFormMode('edit');
        setIsModalOpen(true);
    };

    const handleViewShippingLine = (shippingLine) => {
        setCurrentShippingLine(shippingLine);
        setFormMode('view');
        setIsModalOpen(true);
    };

    const handleDeleteShippingLine = async (id) => {
        if (window.confirm("Are you sure you want to delete this shipping line?")) { // Use custom modal in real app
            try {
                const response = await fetch(`${API_BASE_URL}${id}/`, {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                });
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to delete shipping line');
                }
                setShippingLines(prev => prev.filter(sl => sl.id !== id));
                // alert("Shipping line deleted successfully!"); // Use custom modal for success
            } catch (err) {
                console.error("Error deleting shipping line:", err);
                setError("Failed to delete shipping line.");
            }
        }
    };

    const handleFormClose = (refresh = false) => {
        setIsModalOpen(false);
        setCurrentShippingLine(null); // Clear current shipping line data
        if (refresh) {
            fetchShippingLines(); // Re-fetch data if a save operation occurred
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const toggleRowExpansion = (id) => {
        setExpandedRows(prev => {
            const newExpandedRows = new Set(prev);
            if (newExpandedRows.has(id)) {
                newExpandedRows.delete(id);
            } else {
                newExpandedRows.add(id);
            }
            return newExpandedRows;
        });
    };

    const filteredShippingLines = shippingLines
        .filter(sl => {
            // Line Type filter
            if (lineTypeFilter && sl.line_type !== lineTypeFilter) return false;

            // Deposit Waiver filter
            let waiverValue = '';
            if (sl.line_type === 'AGENT') {
                waiverValue = 'N/A';
            } else if (sl.has_deposit_waiver === true) {
                waiverValue = 'YES';
            } else if (sl.has_deposit_waiver === false) {
                waiverValue = 'NO';
            }
            if (depositWaiverFilter && waiverValue !== depositWaiverFilter) return false;

            // Search filter (existing)
            const term = searchTerm.toLowerCase();
            if (!term) return true;
            const topLevelMatch = Object.entries(sl).some(([key, value]) => {
                if (Array.isArray(value)) return false;
                return String(value).toLowerCase().includes(term);
            });
            if (topLevelMatch) return true;
            const carrierMatch = sl.bl_carriers?.some(carrier =>
                carrier.carrier_name?.toLowerCase().includes(term)
            );
            return carrierMatch;
        });

    const {
        items: sortedAndFilteredShippingLines,
        requestSort,
        getSortIndicator
    } = useSortableTable(filteredShippingLines);

    return (
        <div className="shipping-line-list-container">
            <h2>Shipping Lines List</h2>

            {loading && <p className="loading-message">Loading shipping lines...</p>}
            {error && <p className="error-message">{error}</p>}

            <div
                className="toolbar"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    marginBottom: 16,
                    flexWrap: 'nowrap'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'nowrap' }}>
                    <input
                        type="text"
                        placeholder="SEARCH SHIPPING LINES..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        style={{ minWidth: 220, maxWidth: 260 }}
                    />
                    <Select2Dropdown
                        options={DEPOSIT_WAIVER_OPTIONS}
                        value={depositWaiverFilter}
                        onChange={setDepositWaiverFilter}
                        displayKey="text"
                        valueKey="id"
                        placeholder="DEPOSIT WAIVER"
                        clearable={true}
                        style={{ width: 150, minWidth: 150, maxWidth: 180 }}
                    />
                    <Select2Dropdown
                        options={LINE_TYPE_OPTIONS}
                        value={lineTypeFilter}
                        onChange={setLineTypeFilter}
                        displayKey="text"
                        valueKey="id"
                        placeholder="LINE TYPE"
                        clearable={true}
                        style={{ width: 230, minWidth: 230, maxWidth: 250 }}
                    />
                </div>
                {/* 3. Protect Add button */}
                {hasPermission('SHIPPING LINES', 'can_create') && (
                    <button onClick={handleAddShippingLine} style={{ marginLeft: 'auto', minWidth: 200 }}>
                        Add New Shipping Line
                    </button>
                )}
            </div>
            {!loading && !error && (
                sortedAndFilteredShippingLines.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('name')} className="sortable">
                                    Name {getSortIndicator('name')}
                                </th>
                                <th onClick={() => requestSort('line_type')} className="sortable">
                                    Type {getSortIndicator('line_type')}
                                </th>
                                <th onClick={() => requestSort('phone')} className="sortable">
                                    Phone {getSortIndicator('phone')}
                                </th>
                                <th onClick={() => requestSort('address')} className="sortable">
                                    Address {getSortIndicator('address')}
                                </th>
                                <th onClick={() => requestSort('country')} className="sortable" style={{ textAlign: 'center' }}>
                                    Country {getSortIndicator('country')}
                                </th>
                                <th onClick={() => requestSort('city')} className="sortable">
                                    City {getSortIndicator('city')}
                                </th>                                
                                <th onClick={() => requestSort('has_deposit_waiver')} className="sortable" style={{ textAlign: 'center' }}>
                                    Deposit Waiver {getSortIndicator('has_deposit_waiver')}
                                </th>
                                <th>Details</th>
                                {/* 4. Protect Actions column header */}
                                {(hasPermission('SHIPPING LINES', 'can_view') || hasPermission('SHIPPING LINES', 'can_edit') || hasPermission('SHIPPING LINES', 'can_delete')) && (
                                    <th>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedAndFilteredShippingLines.map((sl) => (
                                <React.Fragment key={sl.id}>
                                    <tr>
                                        <td>{sl.name.toUpperCase()}</td>
                                        <td>{sl.line_type.toUpperCase()}</td>
                                        <td>{sl.phone}</td>
                                        <td>{sl.address.toUpperCase()}</td>
                                        <td style={{ textAlign: 'center' }}>{sl.country.toUpperCase()}</td>
                                        <td style={{ textAlign: 'center' }}>{sl.city.toUpperCase()}</td>                                        
                                        <td style={{ textAlign: 'center' }}>
                                            {sl.line_type === 'AGENT'
                                                ? <span style={{ color: '#888', fontWeight: 'bold' }}>N/A</span>
                                                : (
                                                    sl.has_deposit_waiver === true
                                                        ? <span title="Has Deposit Waiver" style={{ color: 'green', fontSize: '1.2em' }}>&#10003;</span>
                                                        : <span title="No Deposit Waiver" style={{ color: 'red', fontSize: '1.2em' }}>&#10007;</span>
                                                )
                                            }
                                        </td>
                                        <td className="expand-toggle">
                                            {sl.bl_carriers && sl.bl_carriers.length > 0 && (
                                                <button
                                                    className="icon-btn"
                                                    onClick={() => toggleRowExpansion(sl.id)}
                                                    title={expandedRows.has(sl.id) ? 'Collapse Details' : 'Expand Details'}
                                                >
                                                    <i className={`fa ${expandedRows.has(sl.id) ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                                </button>
                                            )}
                                        </td>
                                        {/* 5. Protect Actions cell */}
                                        {(hasPermission('SHIPPING LINES', 'can_view') || hasPermission('SHIPPING LINES', 'can_edit') || hasPermission('SHIPPING LINES', 'can_delete')) && (
                                            <td className="actions">
                                                {hasPermission('SHIPPING LINES', 'can_view') && (
                                                    <button className="icon-btn view-btn" onClick={() => handleViewShippingLine(sl)} title="View">
                                                        <i className="fa fa-eye"></i>
                                                    </button>
                                                )}
                                                {hasPermission('SHIPPING LINES', 'can_edit') && (
                                                    <button className="icon-btn edit-btn" onClick={() => handleEditShippingLine(sl)} title="Edit">
                                                        <i className="fa fa-pencil-alt"></i>
                                                    </button>
                                                )}
                                                {hasPermission('SHIPPING LINES', 'can_delete') && (
                                                    <button className="icon-btn delete-btn" onClick={() => handleDeleteShippingLine(sl.id)} title="Delete">
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                    {expandedRows.has(sl.id) && sl.bl_carriers && sl.bl_carriers.length > 0 && (
                                        <tr className="nested-details-row">
                                            <td colSpan="9">
                                                <div className="nested-details-content" style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                                                    <h4 style={{ margin: 0 }}>BL Carriers:</h4>
                                                    <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'disc' }}>
                                                        {sl.bl_carriers.map((carrier, idx) => (
                                                            <li key={idx}>
                                                                {carrier.carrier_name ? carrier.carrier_name.toUpperCase() : ''}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="no-data">No shipping lines found.</p>
                )
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="modal-close-btn" onClick={() => handleFormClose(false)}>
                            &times;
                        </button>
                        <ShippingLineForm
                            mode={formMode}
                            initialData={currentShippingLine}
                            onClose={handleFormClose}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShippingLineList;
